import { useState } from 'react';
import Image from 'next/image';
import { oneLine } from 'common-tags';
import { FreeMode, Navigation, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { PopUpBase } from '@/atoms';

import Derecha from '@/assets/icons/Derecha.svg';
import Izquierda from '@/assets/icons/Izquierda.svg';

import type { FunctionComponent } from 'react';
import type { Swiper as TypeSwiper } from 'swiper';
import type { TypePopUpGaleria } from '../types';

/**
 * PopUpGaleria
 */

const ThumbsElement = (
  images: string[],
  setThumbsSwiper: (value: any | ((prevVar: any) => any)) => void,
  selectedIndex: number,

  setIndex: (value: number | ((prevVar: number) => number)) => void
): JSX.Element => (
  <Swiper
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    onSwiper={setThumbsSwiper}
    className="absolute h-[108px] w-[90vw] sm:w-[424px]"
    spaceBetween={16}
    breakpoints={{
      0: {
        slidesPerView: 3,
      },
      370: {
        slidesPerView: 4,
      },
    }}
    freeMode
    watchSlidesProgress
    modules={[FreeMode, Navigation, Thumbs]}
    onActiveIndexChange={(e) => setIndex(e.activeIndex)}
    centerInsufficientSlides
  >
    {images.map((item, index) => (
      <SwiperSlide
        className={oneLine`PopUp relative cursor-pointer ${
          selectedIndex === index
            ? 'after:absolute after:bottom-0 after:left-[18px] after:right-[18px] after:block after:h-0.5 after:bg-secondary-400 after:content-[""]'
            : ''
        }`}
        key={index}
      >
        <div
          className={`h-[94px] w-[94px] border-2 ${
            selectedIndex === index ? 'border-white' : 'border-transparent'
          }`}
        >
          <Image
            src={item}
            alt={`Imagen de la vivienda Thumbnail ${index + 1}`}
            width={94}
            height={94}
            objectFit="cover"
            className="Imagen u-aspect-square"
          />
        </div>
      </SwiperSlide>
    ))}
  </Swiper>
);

export const PopUpGaleria: FunctionComponent<TypePopUpGaleria> = ({
  state,
  images,
  setState,
  className,
  title,
  desc,
  initialIndex = 0,
}: TypePopUpGaleria) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<null | TypeSwiper>(null);
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);

  const indexHandler = (number: number) => {
    if (number < 0 || number === images.length) {
      return;
    }
    setSelectedIndex(number);
  };

  const onCloseHanlder = () => {
    setState(!state);
    setThumbsSwiper(null);
  };

  return (
    <PopUpBase
      state={state}
      title={title}
      desc={desc ?? undefined}
      thumbsChildren={ThumbsElement(
        images,
        setThumbsSwiper,
        selectedIndex,
        setSelectedIndex
      )}
      type="galery"
      funcionOnClose={onCloseHanlder}
      className={`bg-white ${className}`}
    >
      <div className="w-full ">
        <Swiper
          initialSlide={initialIndex}
          className="ml-2 h-[60vh] w-[70vw] sm:ml-0 md:h-[70vh]"
          spaceBetween={10}
          navigation={{
            nextEl: '.SiguientePopUpGaleria',
            prevEl: '.AnteriorPopUpGaleria',
          }}
          thumbs={{
            // @see https://github.com/nolimits4web/swiper/issues/5630#issuecomment-1111418301
            swiper:
              thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
          }}
          modules={[FreeMode, Navigation, Thumbs]}
          onSlideChangeTransitionEnd={(e) => indexHandler(e.activeIndex)}
        >
          {images.map((item, index) => (
            <SwiperSlide className="rounded-md" key={index}>
              <Image
                src={item}
                className="h-full w-full rounded-md lg:w-1/2"
                layout="fill"
                alt={`Imagen de la vivienda ${index + 1}`}
                objectFit="cover"
              />
            </SwiperSlide>
          ))}

          <div className="relative -top-1/2 z-20 flex w-full -translate-y-1/2 flex-row items-center justify-between px-1 text-primary-400">
            <Izquierda
              width={30}
              onClick={() => indexHandler(selectedIndex - 1)}
              className="AnteriorPopUpGaleria cursor-pointer"
              role="button"
            />

            <Derecha
              width={30}
              onClick={() => indexHandler(selectedIndex + 1)}
              className="SiguientePopUpGaleria cursor-pointer"
              role="button"
            />
          </div>
        </Swiper>
      </div>
    </PopUpBase>
  );
};

PopUpGaleria.displayName = 'PopUpGaleria';
